import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";

const AD_FORMATS = [
  { value: "INSTAGRAM_STANDARD", label: "Instagram Feed" },
  { value: "INSTAGRAM_STORY", label: "Instagram Story" },
  { value: "DESKTOP_FEED_STANDARD", label: "Facebook Feed" },
  { value: "MOBILE_FEED_STANDARD", label: "Facebook Mobile" },
];

type AdPreviewContentProps = {
  adId: string;
  adAccountId?: string;
  forClientUserId?: string;
};

/**
 * Meta's /previews response embeds a fixed-size iframe (its own pixel
 * width/height per ad_format, e.g. 254x510 for Instagram, ~500x790 for
 * Desktop Feed). Forcing that iframe to CSS width:100% doesn't reflow the
 * cross-origin page inside it — it either leaves blank space around a
 * still-native-sized card (Instagram) or clips it with the iframe's own
 * scrollbar (Facebook formats, wider than our dialog). Instead we let the
 * iframe render at its real native size and scale the whole box down/up
 * with a CSS transform to exactly fill the container, preserving layout.
 */
function extractIframeDimensions(html: string): { width: number; height: number } | null {
  // Scope the width/height lookup to the <iframe ...> tag itself — Meta's markup
  // can include other elements (icons, "Sponsored" badges) with their own width/
  // height attributes before the iframe, and matching against the whole string
  // grabbed those instead, producing a wildly wrong scale (element rendered way
  // too large/small, spilling outside the dialog).
  const iframeTagMatch = html.match(/<iframe\b[^>]*>/i);
  if (!iframeTagMatch) return null;
  const tag = iframeTagMatch[0];
  const widthMatch = tag.match(/\swidth="(\d+)"/);
  const heightMatch = tag.match(/\sheight="(\d+)"/);
  if (!widthMatch || !heightMatch) return null;
  const width = Number(widthMatch[1]);
  const height = Number(heightMatch[1]);
  if (!width || !height) return null;
  return { width, height };
}

export function AdPreviewContent({ adId, adAccountId, forClientUserId }: AdPreviewContentProps) {
  const { t } = useTranslation();
  const [adFormat, setAdFormat] = useState(AD_FORMATS[0].value);
  const stageRef = useRef<HTMLDivElement>(null);
  const [stageWidth, setStageWidth] = useState(0);

  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    // Measure synchronously before paint (useLayoutEffect, not useEffect) so a
    // cache-hit render (data already available on mount, e.g. re-opening the
    // dialog within staleTime) doesn't flash one frame at scale=1 before the
    // observer's first callback corrects stageWidth.
    setStageWidth(el.getBoundingClientRect().width);
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width) setStageWidth(width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const { data, isLoading, error } = useQuery<{ body: string }>({
    queryKey: ["/api/dashboard/meta-ads/ad-preview", adId, adFormat, adAccountId, forClientUserId],
    queryFn: () => {
      const params = new URLSearchParams({ format: adFormat });
      if (adAccountId) params.set("adAccountId", adAccountId);
      if (forClientUserId) params.set("forClientUserId", forClientUserId);
      return apiRequest(
        "GET",
        `/api/dashboard/meta-ads/ad-preview/${encodeURIComponent(adId)}?${params.toString()}`
      );
    },
    staleTime: 1000 * 60 * 10,
    retry: 1,
  });

  const sanitizedBody = useMemo(
    () =>
      data?.body
        ? DOMPurify.sanitize(data.body, {
            ADD_TAGS: ["iframe"],
            ADD_ATTR: ["allow", "allowfullscreen", "frameborder", "scrolling"],
          })
        : null,
    [data?.body]
  );
  const dimensions = data?.body ? extractIframeDimensions(data.body) : null;
  const rawScale = dimensions && stageWidth ? stageWidth / dimensions.width : 1;
  // Only ever shrink to fit, never upscale: a narrow native creative (e.g. Instagram,
  // ~254px) blown up to fill a wider dialog also blows up its height by the same
  // factor (uniform scale) — that made the whole dialog balloon so tall its own
  // close button scrolled off-screen. A little side margin beats a broken dialog.
  const scale = Number.isFinite(rawScale) && rawScale > 0 ? Math.min(1, rawScale) : 1;

  return (
    <div>
      <div className="flex gap-1.5 mb-3">
        {AD_FORMATS.map((f) => (
          <button
            key={f.value}
            onClick={() => setAdFormat(f.value)}
            className={`text-xs px-2.5 py-1 rounded transition-colors ${
              adFormat === f.value
                ? "bg-[#ef3e29] text-foreground"
                : "bg-muted/50 text-muted-foreground hover:bg-accent hover:text-foreground"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>
      <div
        ref={stageRef}
        className="min-h-[300px] flex items-center justify-center overflow-hidden rounded"
      >
        {isLoading ? (
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <Loader2 className="w-4 h-4 animate-spin" />
            {t("dashboard.loading.preview")}
          </div>
        ) : error ? (
          <div className="text-muted-foreground text-sm text-center">
            <p className="text-white/50 mb-1">{t("dashboard.adPreview.unavailable")}</p>
            <p className="text-xs text-muted-foreground">
              {t("dashboard.adPreview.unavailableHint")}
            </p>
          </div>
        ) : sanitizedBody && dimensions ? (
          <div
            style={{
              width: dimensions.width * scale,
              height: dimensions.height * scale,
              maxWidth: "100%",
              overflow: "hidden",
            }}
          >
            <div
              className="[&_iframe]:border-0 [&_iframe]:block"
              style={{
                width: dimensions.width,
                height: dimensions.height,
                transform: `scale(${scale})`,
                transformOrigin: "top left",
              }}
              dangerouslySetInnerHTML={{ __html: sanitizedBody }}
            />
          </div>
        ) : sanitizedBody ? (
          // Fallback if we couldn't parse native dimensions from Meta's markup:
          // clamp to the container width instead of forcing w-full, so a wider-
          // than-expected embed can never overflow the dialog even without the
          // scale math above (see extractIframeDimensions' doc comment).
          <div
            style={{ maxWidth: "100%", overflow: "hidden" }}
            className="[&_iframe]:max-w-full [&_iframe]:border-0 [&_iframe]:rounded"
            dangerouslySetInnerHTML={{ __html: sanitizedBody }}
          />
        ) : null}
      </div>
    </div>
  );
}
